import * as XLSX from "xlsx";
import { isAxiosError } from "axios";

import {
  ExtractMessagesOptions,
  extractMessagesFromPayload,
  extractMessagesFromStringPayload,
  parseErrorDetailString,
  stripHtml
} from "helpers/apiError";
import { uploadFRDocsAPI } from "services/api/haulpay";

const BULK_UPLOAD_ROW_CHUNK_SIZE = 10;
const BULK_UPLOAD_MAX_RETRIES = 3;
const LOAD_NUMBER_COLUMN_KEYS = ["Load Number (Required)", "Load Number", "Load Id"];

type BulkUploadEntry = [string, string];
type BulkUploadStatus = "waiting" | "uploading" | "success" | "error";

interface ParsedSpreadsheet {
  header: unknown[];
  rows: unknown[][];
  isCsv: boolean;
  sheetName: string;
}

interface BulkUploadChunk {
  blob: Blob;
  rows: BulkUploadStatusItem[];
}

export interface BulkUploadResult {
  success: BulkUploadEntry[];
  fail: BulkUploadEntry[];
}

export interface BulkUploadStatusItem {
  id: string;
  loadIdentifier: string;
  rowNumber: number;
  status: BulkUploadStatus;
  message?: string;
}

const isBlankCell = (cell: unknown) =>
  cell === null || cell === undefined || (typeof cell === "string" && cell.trim() === "");

const isEmptyRow = (row: unknown[]) => row.every(isBlankCell);

const getFileExtension = (file: File) => file.name.split(".").pop()?.toLowerCase() ?? "";

const isCsvFile = (file: File) => {
  const extension = getFileExtension(file);
  return file.type.includes("csv") || extension === "csv";
};

const isExcelFile = (file: File) => {
  const extension = getFileExtension(file);
  return (
    file.type.includes("excel") ||
    file.type.includes("spreadsheetml") ||
    extension === "xls" ||
    extension === "xlsx"
  );
};

const normalizeLoadIdentifier = (value: unknown) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value).trim();
};

const normalizeCustomerMessage = (message: string, fieldName?: string) => {
  const cleanMessage = stripHtml(message);

  if (
    fieldName === "user_load_number" &&
    cleanMessage.includes("used by another funding request from the same debtor")
  ) {
    return "This load number is already being used on another funding request for this customer. Please update the load number and try again.";
  }

  if (cleanMessage.includes("Debtor has been declined")) {
    return "This customer is not approved for a factored funding request. Update the funding request type to Non-Factored and try again.";
  }

  return cleanMessage;
};

const BULK_UPLOAD_ERROR_OPTIONS: ExtractMessagesOptions = {
  transformMessage: normalizeCustomerMessage
};

const extractBulkUploadMessagesFromUnknownPayload = (
  payload: unknown,
  isCarrierSubscribed: boolean
) => {
  if (Array.isArray(payload)) {
    if (Array.isArray(payload[0])) {
      return formatBulkUploadMessages(payload as BulkUploadEntry[], isCarrierSubscribed);
    }

    return payload.flatMap((item) =>
      extractMessagesFromPayload(item, undefined, BULK_UPLOAD_ERROR_OPTIONS)
    );
  }

  if (typeof payload === "object" && payload !== null) {
    return extractMessagesFromPayload(payload, undefined, BULK_UPLOAD_ERROR_OPTIONS);
  }

  if (typeof payload === "string") {
    const stringMessages = extractMessagesFromStringPayload(
      payload,
      undefined,
      BULK_UPLOAD_ERROR_OPTIONS
    );
    return stringMessages.length
      ? stringMessages
      : [appendDeclinedDebtorMessage(parseErrorDetailString(payload))];
  }

  return [];
};

const readHeaderRow = (sheet: XLSX.WorkSheet, range: XLSX.Range) => {
  const header: unknown[] = [];

  for (let column = range.s.c; column <= range.e.c; column += 1) {
    header[column - range.s.c] = sheet[XLSX.utils.encode_cell({ r: range.s.r, c: column })]?.v;
  }

  return header;
};

const clampSheetToLastLoadNumberRow = (sheet: XLSX.WorkSheet) => {
  const declaredRef = sheet["!ref"];

  if (!declaredRef) {
    return;
  }

  const range = XLSX.utils.decode_range(declaredRef);
  const loadNumberColumnIndex = getLoadNumberColumnIndex(readHeaderRow(sheet, range));

  if (loadNumberColumnIndex < 0) {
    return;
  }

  const loadNumberColumn = range.s.c + loadNumberColumnIndex;
  let lastRow = range.s.r;

  for (const address of Object.keys(sheet)) {
    if (address.charAt(0) === "!") {
      continue;
    }

    const { r, c } = XLSX.utils.decode_cell(address);

    if (c === loadNumberColumn && r > lastRow && !isBlankCell(sheet[address]?.v)) {
      lastRow = r;
    }
  }

  if (lastRow < range.e.r) {
    sheet["!ref"] = XLSX.utils.encode_range({ s: range.s, e: { r: lastRow, c: range.e.c } });
  }
};

const parseSpreadsheet = async (file: File): Promise<ParsedSpreadsheet> => {
  const isCsv = isCsvFile(file);
  const workbook = isCsv
    ? XLSX.read(await file.text(), { type: "string" })
    : XLSX.read(await file.arrayBuffer(), { type: "array" });
  const sheetName = workbook.SheetNames[0];

  if (!sheetName) {
    return { header: [], rows: [], isCsv, sheetName: "Sheet1" };
  }

  const sheet = workbook.Sheets[sheetName];
  clampSheetToLastLoadNumberRow(sheet);
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1 });
  const [header, ...dataRows] = rows;

  if (!header) {
    return { header: [], rows: [], isCsv, sheetName };
  }

  const loadNumberColumnIndex = getLoadNumberColumnIndex(header);
  const hasLoadNumber = (row: unknown[]) =>
    loadNumberColumnIndex < 0 || !isBlankCell(row[loadNumberColumnIndex]);

  return {
    header,
    rows: dataRows.filter((row) => !isEmptyRow(row) && hasLoadNumber(row)),
    isCsv,
    sheetName
  };
};

const getLoadNumberColumnIndex = (header: unknown[]) =>
  header.findIndex((column) => LOAD_NUMBER_COLUMN_KEYS.includes(normalizeLoadIdentifier(column)));

const buildQueueItems = (header: unknown[], rows: unknown[][]): BulkUploadStatusItem[] => {
  const loadNumberColumnIndex = getLoadNumberColumnIndex(header);

  return rows.map((row, index) => {
    const rowNumber = index + 2;
    const loadIdentifier =
      loadNumberColumnIndex >= 0 ? normalizeLoadIdentifier(row[loadNumberColumnIndex]) : "";

    return {
      id: `${rowNumber}-${loadIdentifier || "row"}`,
      loadIdentifier,
      rowNumber,
      status: "waiting"
    };
  });
};

const buildChunkBlob = (
  header: unknown[],
  rows: unknown[][],
  sheetName: string,
  isCsv: boolean
): Blob => {
  const chunkRows = [header, ...rows];

  if (isCsv) {
    const chunkSheet = XLSX.utils.aoa_to_sheet(chunkRows);
    const csvChunk = XLSX.utils.sheet_to_csv(chunkSheet);
    return new Blob([csvChunk], { type: "text/csv" });
  }

  const newWorkbook = XLSX.utils.book_new();
  const newSheet = XLSX.utils.aoa_to_sheet(chunkRows);
  XLSX.utils.book_append_sheet(newWorkbook, newSheet, sheetName);
  const chunkFile = XLSX.write(newWorkbook, { type: "array", bookType: "xlsx" });

  return new Blob([chunkFile], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });
};

const splitFileIntoChunks = async (
  file: File,
  chunkSize = BULK_UPLOAD_ROW_CHUNK_SIZE
): Promise<{ chunks: BulkUploadChunk[]; queueItems: BulkUploadStatusItem[]; isCsv: boolean }> => {
  if (!isExcelFile(file) && !isCsvFile(file)) {
    throw new Error("Unsupported file type. Please upload a CSV or Excel file.");
  }

  const { header, rows, isCsv, sheetName } = await parseSpreadsheet(file);
  const queueItems = buildQueueItems(header, rows);
  const chunks: BulkUploadChunk[] = [];

  for (let index = 0; index < rows.length; index += chunkSize) {
    chunks.push({
      blob: buildChunkBlob(header, rows.slice(index, index + chunkSize), sheetName, isCsv),
      rows: queueItems.slice(index, index + chunkSize)
    });
  }

  return { chunks, queueItems, isCsv };
};

const uploadChunk = async (
  file: File,
  retries = BULK_UPLOAD_MAX_RETRIES
): Promise<BulkUploadResult> => {
  let attempt = 0;

  while (attempt < retries) {
    try {
      const response = await uploadFRDocsAPI(file);
      return response.data as BulkUploadResult;
    } catch (error) {
      attempt += 1;
      if (attempt >= retries) {
        throw error;
      }
    }
  }

  throw new Error("Bulk upload failed.");
};

const coerceBulkUploadResult = (value: unknown): BulkUploadResult => {
  const result = value as BulkUploadResult | undefined;

  const filterValidTuples = (arr: unknown): BulkUploadEntry[] => {
    if (!Array.isArray(arr)) return [];
    return arr.filter(
      (entry): entry is BulkUploadEntry =>
        Array.isArray(entry) &&
        entry.length === 2 &&
        typeof entry[0] === "string" &&
        typeof entry[1] === "string"
    );
  };

  return {
    success: filterValidTuples(result?.success),
    fail: filterValidTuples(result?.fail)
  };
};

const getPrimaryBulkUploadErrorMessage = (error: unknown, isCarrierSubscribed: boolean) =>
  extractBulkUploadErrorMessages(error, isCarrierSubscribed)[0] ?? "Bulk upload failed.";

const updateQueueItems = (
  queueItems: BulkUploadStatusItem[],
  rowIds: string[],
  status: BulkUploadStatus,
  message?: string
): BulkUploadStatusItem[] =>
  queueItems.map<BulkUploadStatusItem>((item) =>
    rowIds.includes(item.id)
      ? {
          ...item,
          status,
          ...(message ? { message } : {})
        }
      : item
  );

const cancelPendingQueueItems = (queueItems: BulkUploadStatusItem[]) =>
  updateQueueItems(
    queueItems,
    queueItems
      .filter((item) => item.status === "waiting" || item.status === "uploading")
      .map((item) => item.id),
    "error",
    "Upload canceled before this row was submitted."
  );

const applyChunkResult = (
  queueItems: BulkUploadStatusItem[],
  chunkRows: BulkUploadStatusItem[],
  result: BulkUploadResult,
  fallbackMessage?: string
): BulkUploadStatusItem[] => {
  const nextItems: BulkUploadStatusItem[] = [...queueItems];
  const chunkRowIds = new Set(chunkRows.map((row) => row.id));
  const availableRows = new Map<string, string[]>();

  chunkRows.forEach((row) => {
    const normalizedIdentifier = normalizeLoadIdentifier(row.loadIdentifier);
    const existingRows = availableRows.get(normalizedIdentifier) ?? [];
    existingRows.push(row.id);
    availableRows.set(normalizedIdentifier, existingRows);
  });

  const applyEntries = (entries: BulkUploadEntry[], status: BulkUploadStatus) => {
    entries.forEach(([identifier, detail]) => {
      const key = normalizeLoadIdentifier(identifier);
      const matchingRows = availableRows.get(key);
      const rowId = matchingRows?.shift();

      if (!rowId) {
        return;
      }

      const index = nextItems.findIndex((item) => item.id === rowId);
      if (index >= 0) {
        nextItems[index] = {
          ...nextItems[index],
          status,
          message: normalizeBulkUploadEntryDetail(detail)
        };
      }
    });
  };

  applyEntries(result.success ?? [], "success");
  applyEntries(result.fail ?? [], "error");

  return nextItems.map<BulkUploadStatusItem>((item) => {
    if (!chunkRowIds.has(item.id) || item.status !== "uploading") {
      return item;
    }

    return {
      ...item,
      status: "error",
      message: fallbackMessage ?? item.message ?? "Bulk upload failed."
    };
  });
};

export const uploadBulkFundingRequestFile = async (
  file: File,
  onProgress?: (items: BulkUploadStatusItem[]) => void,
  isCarrierSubscribed = false,
  signal?: AbortSignal
): Promise<BulkUploadResult> => {
  const { chunks, queueItems: initialQueueItems, isCsv } = await splitFileIntoChunks(file);
  const response: BulkUploadResult = { success: [], fail: [] };
  let queueItems = initialQueueItems;

  if (!queueItems.length) {
    throw new Error("No data rows found in the selected file.");
  }

  onProgress?.(queueItems);

  for (const chunk of chunks) {
    if (signal?.aborted) {
      queueItems = cancelPendingQueueItems(queueItems);
      onProgress?.(queueItems);
      return response;
    }

    queueItems = updateQueueItems(
      queueItems,
      chunk.rows.map((row) => row.id),
      "uploading"
    );
    onProgress?.(queueItems);

    const chunkFile = new File([chunk.blob], `chunk.${isCsv ? "csv" : "xlsx"}`, {
      type:
        chunk.blob.type ||
        (isCsv ? "text/csv" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
    });

    try {
      const chunkResponse = await uploadChunk(chunkFile);

      response.success.push(...(chunkResponse.success ?? []));
      response.fail.push(...(chunkResponse.fail ?? []));
      queueItems = applyChunkResult(queueItems, chunk.rows, chunkResponse);
      onProgress?.(queueItems);
    } catch (error) {
      const errorResult = isAxiosError(error)
        ? coerceBulkUploadResult(error.response?.data)
        : { success: [], fail: [] };

      response.success.push(...(errorResult.success ?? []));
      response.fail.push(...(errorResult.fail ?? []));
      queueItems = applyChunkResult(
        queueItems,
        chunk.rows,
        errorResult,
        getPrimaryBulkUploadErrorMessage(error, isCarrierSubscribed)
      );
      onProgress?.(queueItems);
      throw error;
    }
  }

  return response;
};

const appendDeclinedDebtorMessage = (message: string) => {
  if (!message.includes("Debtor has been declined")) {
    return message;
  }

  return message;
};

const normalizeBulkUploadEntryDetail = (detail: unknown) => {
  const messages = extractMessagesFromPayload(detail, undefined, BULK_UPLOAD_ERROR_OPTIONS).map(
    appendDeclinedDebtorMessage
  );

  if (messages.length) {
    return messages.join("\n");
  }

  if (detail == null) {
    return "Unknown error";
  }

  return appendDeclinedDebtorMessage(String(detail));
};

export const formatBulkUploadMessages = (
  items: Array<string[] | BulkUploadEntry>,
  isCarrierSubscribed: boolean
) => {
  const loadType = isCarrierSubscribed ? "Load Number" : "Load Id";

  return items.map((item) => {
    const rawLoadIdentifier = item?.[0] as string | number | undefined;
    const rawDetail = item?.[1];
    const loadIdentifier = rawLoadIdentifier == null ? "" : String(rawLoadIdentifier);
    const detail = normalizeBulkUploadEntryDetail(rawDetail);

    if (loadIdentifier === "" || loadIdentifier === "0") {
      return detail;
    }

    return `${loadType}: ${loadIdentifier}, ${detail}`;
  });
};

export const isBulkUploadPermissionError = (error: unknown) => {
  return isAxiosError(error)
    ? error.response?.status === 403 || error.message.includes("403")
    : false;
};

export const extractBulkUploadErrorMessages = (
  error: unknown,
  isCarrierSubscribed: boolean
): string[] => {
  if (isAxiosError(error)) {
    const failEntries = error.response?.data?.fail;
    const successEntries = error.response?.data?.success;

    if (Array.isArray(failEntries)) {
      const failMessages = Array.isArray(failEntries[0])
        ? formatBulkUploadMessages(failEntries, isCarrierSubscribed)
        : [appendDeclinedDebtorMessage(failEntries.join("-"))];

      const successMessages = Array.isArray(successEntries)
        ? formatBulkUploadMessages(successEntries, isCarrierSubscribed)
        : [];

      return [...failMessages, ...successMessages];
    }

    const payloadMessages = extractBulkUploadMessagesFromUnknownPayload(
      typeof error.response?.data?.detail === "string"
        ? error.response.data.detail
        : error.response?.data,
      isCarrierSubscribed
    );

    if (payloadMessages.length) {
      return payloadMessages.map(appendDeclinedDebtorMessage);
    }
  }

  if (error instanceof Error) {
    return [appendDeclinedDebtorMessage(parseErrorDetailString(error.message))];
  }

  return ["Bulk upload failed."];
};
